import { ByUiTheme } from '@/components/shared/ByUiTheme/ByUiTheme';
import { lazy, Suspense } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { PageLoader } from "@/components/shared/PageLoader/PageLoader";
import { PreviewProvider } from "@/contexts/shared/PreviewContext";

import { tr } from '@/i18n';
const SeminarCheckinPage = lazy(() => import("@/pages/User/Course/SeminarCheckinPage/SeminarCheckinPage").then(m => ({ default: m.SeminarCheckinPage })));
const UserProfilePage = lazy(() => import("@/pages/User/UserProfile/UserProfilePage").then(m => ({ default: m.UserProfilePage })));
const CourseStartPage = lazy(() => import("@/pages/User/Course/CourseStartPage/CourseStartPage").then(m => ({ default: m.CourseStartPage })));
const CourseStartPageClassic = lazy(() => import("@/pages/User/Course/CourseStartPage/CourseStartPageClassic").then(m => ({ default: m.CourseStartPageClassic })));
const ExplorePage = lazy(() => import("@/pages/User/ExplorePage").then(m => ({ default: m.ExplorePage })));
const NotificationsPage = lazy(() => import("@/pages/User/Notifications/NotificationsPage").then(m => ({ default: m.NotificationsPage })));
const LeaAssistantPage = lazy(() => import("@/pages/User/LeaAssistantPage").then(m => ({ default: m.LeaAssistantPage })));
const MyPdpsPage = lazy(() => import("@/pages/User/Pdp/MyPdpsPage").then(m => ({ default: m.MyPdpsPage })));
const MyReportsPage = lazy(() => import("@/pages/User/Reports/MyReportsPage").then(m => ({ default: m.MyReportsPage })));
const SharedReportPage = lazy(() => import("@/pages/User/Reports/SharedReportPage").then(m => ({ default: m.SharedReportPage })));
const PdpDetailPage = lazy(() => import("@/pages/User/Pdp/PdpDetailPage").then(m => ({ default: m.PdpDetailPage })));
const ProgramPage = lazy(() => import("@/pages/User/ProgramPage/ProgramPage").then(m => ({ default: m.ProgramPage })));
const ProgramDetailPage = lazy(() => import("@/pages/User/ProgramPage/ProgramDetailPage").then(m => ({ default: m.ProgramDetailPage })));
const ProgramDetailPageClassic = lazy(() => import("@/pages/User/ProgramPage/ProgramDetailPageClassic").then(m => ({ default: m.ProgramDetailPageClassic })));
const SavedPage = lazy(() => import("@/pages/User/SavedPage").then(m => ({ default: m.SavedPage })));
const MyLearningPage = lazy(() => import("@/pages/User/MyLearningPage").then(m => ({ default: m.MyLearningPage })));
const DashboardPage = lazy(() => import("@/pages/User/DashboardPage").then(m => ({ default: m.DashboardPage })));
const AnnouncementPage = lazy(() => import("@/pages/User/AnnouncementPage/AnnouncementPage").then(m => ({ default: m.AnnouncementPage })));
const CalendarPage = lazy(() => import("@/pages/User/CalendarPage/CalendarPage").then(m => ({ default: m.CalendarPage })));
const GoalsPage = lazy(() => import("@/pages/User/UserGoalsPage/UserGoalsPage").then(m => ({ default: m.GoalsPage })));
const TeamGoalsPage = lazy(() => import("@/pages/User/TeamGoalsPage/TeamGoalsPage").then(m => ({ default: m.TeamGoalsPage })));
const ProficiencyPage = lazy(() => import("@/pages/User/ProficiencyPage/ProficiencyPage").then(m => ({ default: m.ProficiencyPage })));
const SkillsToRatePage = lazy(() => import("@/components/user/Proficiency/SkillsToRatePage").then(m => ({ default: m.SkillsToRatePage })));
const ProficiencyDetailPage = lazy(() => import("@/components/user/Proficiency/ProficiencyDetailPage").then(m => ({ default: m.ProficiencyDetailPage })));
const CareerPage = lazy(() => import("@/pages/User/CareerPathPage/CareerPathPage").then(m => ({ default: m.CareerPage })));
const MentorsPage = lazy(() => import("@/pages/User/MentorsPage/MentorsPage").then(m => ({ default: m.MentorsPage })));
const TeamSkillPage = lazy(() => import("@/components/user/TeamHub/TeamSkillPage").then(m => ({ default: m.TeamSkillPage })));
const TeamTalentGrid = lazy(() => import("@/components/admin/Performance/TalentGrid/TalentGridPage").then(m => ({ default: m.TalentGridPage })));
const TeamComplianceReport = lazy(() => import("@/components/admin/Insights/system/ComplianceReport/ComplianceReport").then(m => ({ default: () => <m.ComplianceReport scope="team" /> })));
const TeamHubPage = lazy(() => import("@/pages/User/TeamHubPage/TeamHubPage").then(m => ({ default: m.TeamHubPage })));
const TeachingPage = lazy(() => import("@/pages/User/TeachingPage/TeachingPage").then(m => ({ default: m.TeachingPage })));
const EmployeeGoalsReviewPage = lazy(() => import("@/components/user/Manager/Goals/EmployeeGoalsReviewPage").then(m => ({ default: m.EmployeeGoalsReviewPage })));
const EmployeeGoalsReviewPageClassic = lazy(() => import("@/components/user/Manager/Goals/EmployeeGoalsReviewPageClassic").then(m => ({ default: m.EmployeeGoalsReviewPageClassic })));
const OrgGoalsPage = lazy(() => import("@/components/user/Manager/Goals/OrgGoalsPage").then(m => ({ default: m.OrgGoalsPage })));

const CoursePlayerPage = lazy(() => import("@/pages/User/Course/CoursePlayerPage/CoursePlayerPage").then(m => ({ default: m.CoursePlayerPage })));
const BlockIntroPage = lazy(() => import("@/components/user/CoursePlayer/BlockIntroPage/BlockIntroPage").then(m => ({ default: m.BlockIntroPage })));
const DynamicLessonRenderer = lazy(() => import("@/components/user/CoursePlayer/DynamicLessonRenderer").then(m => ({ default: m.DynamicLessonRenderer })));
const ForumPage = lazy(() => import("@/components/user/Forums/ForumPage").then(m => ({ default: m.ForumPage })));
const DiscussionPage = lazy(() => import("@/components/user/Forums/DiscussionPage").then(m => ({ default: m.DiscussionPage })));
const MarkingPage = lazy(() => import("@/pages/User/Marking/MarkingPage").then(m => ({ default: m.MarkingPage })));
const OjtEvaluationsPage = lazy(() => import("@/pages/User/OjtEvaluations/OjtEvaluationsPage").then(m => ({ default: m.OjtEvaluationsPage })));
const CommunityListPage = lazy(() => import("@/components/user/Community/CommunityListPage").then(m => ({ default: m.CommunityListPage })));
const CommunityWorkspacePage = lazy(() => import("@/pages/User/Community/CommunityWorkspacePage").then(m => ({ default: m.CommunityWorkspacePage })));
const MyFormsPage = lazy(() => import("@/pages/User/Forms/MyFormsPage").then(m => ({ default: m.MyFormsPage })));
const FormInstancePage = lazy(() => import("@/pages/User/Forms/FormInstancePage").then(m => ({ default: m.FormInstancePage })));
const MessagesPage = lazy(() => import("@/pages/User/Messages/MessagesPage").then(m => ({ default: m.MessagesPage })));

export const UserRoutes = () => {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route index element={<Navigate to="dashboard" replace />} />

        <Route path="profile/preview" element={<PreviewProvider><UserProfilePage /></PreviewProvider>} />
        <Route path="profile/:id" element={<PreviewProvider><UserProfilePage /></PreviewProvider>} />
        <Route path="profile" element={<PreviewProvider><UserProfilePage /></PreviewProvider>} />

        <Route path="notifications" element={<NotificationsPage />} />
        <Route path="messages" element={<MessagesPage />} />

        {/* Essay marking queue (marker role) */}
        <Route path="marking" element={<MarkingPage />} />

        {/* OJT evaluation inbox (SME role) */}
        <Route path="ojt-evaluations" element={<OjtEvaluationsPage />} />

        <Route path="course-start" element={<ByUiTheme bento={<CourseStartPage />} classic={<CourseStartPageClassic />} />} />
        <Route path="course-start/:id" element={<ByUiTheme bento={<CourseStartPage />} classic={<CourseStartPageClassic />} />} />

        {/* New activity-based player route */}
        <Route path="course/:courseId/activity/:activityId" element={<CoursePlayerPage />} />
        {/* Opened from a seminar session's QR code: checks the learner in. */}
        <Route path="seminar-checkin/:sessionId" element={<SeminarCheckinPage />} />

        {/* Legacy lesson routes (backward compatibility) */}
        <Route path="course/:courseId" element={<CoursePlayerPage />}>
          <Route path="lesson/:lessonId" element={<BlockIntroPage />} />
          <Route path="lesson/:lessonId/play" element={<DynamicLessonRenderer />} />
        </Route>

        <Route path="courses" element={<ExplorePage />} />

        <Route path="lea-assistant/*" element={<LeaAssistantPage />} />

        <Route path="program-start" element={<ProgramPage />} />
        <Route path="program-start/:id" element={<ByUiTheme bento={<ProgramDetailPage />} classic={<ProgramDetailPageClassic />} />} />
        <Route path="learning-path/:id" element={<ByUiTheme bento={<ProgramDetailPage />} classic={<ProgramDetailPageClassic />} />} />

        <Route path="pdp" element={<MyPdpsPage />} />
        <Route path="pdp/:id" element={<PdpDetailPage />} />

        <Route path="saved" element={<SavedPage />} />
        <Route path="reports" element={<MyReportsPage />} />
        <Route path="reports/view/:slug" element={<SharedReportPage />} />
        <Route path="my-courses" element={<MyLearningPage />} />
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="dashboard/:id" element={<DashboardPage />} />
        <Route path="announcement" element={<AnnouncementPage />} />
        <Route path="calendar" element={<CalendarPage />} />
        <Route path="resources" element={<div>{tr("ui.resources_content", "Resources Content")}</div>} />

        <Route path="forms" element={<MyFormsPage />} />
        <Route path="forms/:id" element={<FormInstancePage />} />

        <Route path="skills" element={<ProficiencyPage />} />
        <Route path="skills-rating" element={<SkillsToRatePage />} />
        {/* Earlier address of Skills Rating (rating-request notifications sent before the move). */}
        <Route path="skills/rate" element={<SkillsToRatePage />} />
        <Route path="skills/:kind/:id" element={<ProficiencyDetailPage />} />
        <Route path="career" element={<CareerPage />} />
        <Route path="mentors" element={<MentorsPage />} />
        <Route path="team-hub" element={<TeamHubPage />} />
        <Route path="team-hub/compliance" element={<TeamComplianceReport />} />
        <Route path="team-hub/skill/:id" element={<TeamSkillPage />} />
        <Route path="talent-grid" element={<TeamTalentGrid />} />
        <Route path="teaching" element={<TeachingPage />} />
        <Route path="goals" element={<GoalsPage />} />
        {/* Goals are created/edited in the dialog on /user/goals; keep old links working. */}
        <Route path="goals/add" element={<Navigate to="/user/goals?create=1" replace />} />
        <Route path="goals/edit/:id" element={<Navigate to="/user/goals" replace />} />
        <Route path="team/goals" element={<TeamGoalsPage />} />
        <Route path="team/org-goals" element={<OrgGoalsPage />} />
        <Route path="team/goals/:userId" element={<ByUiTheme bento={<EmployeeGoalsReviewPage />} classic={<EmployeeGoalsReviewPageClassic />} />} />

        <Route path="forum/:forumId" element={<ForumPage />} />
        <Route path="discussion/:discussionId" element={<DiscussionPage />} />

        {/* Communities (post detail renders inline inside the workspace) */}
        <Route path="communities" element={<CommunityListPage />} />
        <Route path="community/:communityId" element={<CommunityWorkspacePage />} />
        {/* Deep-linkable post page (YouTube-style; renders inside the workspace) */}
        <Route path="community/:communityId/post/:postId" element={<CommunityWorkspacePage />} />

        {/* Absolute redirect so unknown routes can't recursively append
         *  "/profile" to the URL when the user is already inside /user. */}
        <Route path="*" element={<Navigate to="/user/profile" replace />} />
      </Routes>
    </Suspense>
  );
};